import {
  Card,
  CardHeader,
  CardTitle,
  EventType,
  Timeline,
  TimelineEvent,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import { useMemo } from 'react';
import { useDateTimeFormat } from '../modules/conversions/useDateFormat';
import { cn } from '@ais-ui/utils';

export type TimelineContainerProps = {
  events: TimelineEvent[];
  eventTypes: EventType[]; // All possible event types
  className?: string;
  clusterThreshold?: number; // Distance threshold for clustering (in percentage)
  showLegend?: boolean; // Whether to show the legend
  title: string; // Title for the timeline
  durationDays?: number; // Duration in days for the timeline
};

export const TimelineContainer = ({
  events,
  eventTypes,
  className,
  clusterThreshold = 5,
  showLegend = true,
  title,
  durationDays,
}: TimelineContainerProps) => {
  const { formatDateWithTimeZone } = useDateTimeFormat();

  const [startDate, endDate] = useMemo(() => {
    if (!events.length) return [null, null];

    const sortedDates = [...events].sort(
      (a, b) => a.date.getTime() - b.date.getTime()
    );
    const start = sortedDates[0].date;
    const end = sortedDates[sortedDates.length - 1].date;

    return [start, end];
  }, [events]);

  const subTitle = useMemo(() => {
    if (durationDays) {
      return `${formatDateWithTimeZone(startDate)} to ${formatDateWithTimeZone(
        endDate
      )} (${durationDays} days)`;
    }

    return `${formatDateWithTimeZone(startDate)} to ${formatDateWithTimeZone(
      endDate
    )}`;
  }, [durationDays, formatDateWithTimeZone, startDate, endDate]);

  return (
    <Card className="p-4 space-y-2">
      <CardHeader>
        <div className="flex justify-between w-full">
          <div className="flex flex-col space-y-2">
            <CardTitle>{title}</CardTitle>
            <div className="flex font-medium text-muted-foreground text-sm">
              {subTitle}
            </div>
          </div>
          {showLegend && (
            <div className="pb-10 flex flex-wrap gap-4 justify-center pr-4">
              {eventTypes.map((type) => (
                <Tooltip key={type.id}>
                  <TooltipTrigger>
                    <div
                      key={type.id}
                      className={cn(
                        'flex items-center gap-2 cursor-default',
                        type.disabled && 'opacity-50'
                      )}
                    >
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: type.color }}
                      />
                      <span className="text-sm">{type.name}</span>
                    </div>
                  </TooltipTrigger>
                  {type.disabled && (
                    <TooltipContent>Missing permission</TooltipContent>
                  )}
                </Tooltip>
              ))}
            </div>
          )}
        </div>
      </CardHeader>
      <Timeline
        events={events}
        eventTypes={eventTypes}
        className={className}
        clusterThreshold={clusterThreshold}
        durationDays={durationDays}
      />
    </Card>
  );
};
